<template>
  <section class="find-store">
    <div class="find-store__label-area">
      <span class="find-store__label-text">Sök efter butik</span>
      <button
        class="button button--32 button--text-icon button--transparent"
        @click="getStoresByLocation"
      >
        <svgicon
          class="my-position-icon"
          name="Positions/MyPosition"
        />
        Butiker nära dig
      </button>
    </div>
    <div class="find-store__search-input input-text input-text--simple input-text--48">
      <div class="input-text__elements">
        <label
          class="input-text__label"
          for="store-search"
          v-text="'Sök på ort eller butik'"
        />
        <input
          id="store-search"
          v-model="searchQuery"
          class="input-text__element store-search__input"
          type="search"
          placeholder="Sök på ort eller butik"
          @keyup.enter="getStores()"
        >
        <svgicon
          class="search-icon"
          name="Navigation/Search"
        />
      </div>
      <svgicon
        class="search-icon"
        name="Navigation/Search"
      />
    </div>
    <div v-if="stores || loading">
      <div
        v-if="loading"
        class="find-store__loader"
      >
        <spinner />
      </div>

      <div
        v-if="stores"
        class="find-store__results"
      >
        <p
          v-if="stores.length === 0"
          class="store-list__heading"
        >
          0 Resultat
        </p>
        <searchNotification
          v-if="stores.length === 0"
        />
        <store-list
          v-if="stores.length > 0"
          :stores="stores"
          :heading="resultHeading"
          :current-store-id="currentStoreId"
          @selectedStore="emitSelectedStore"
        />
        <show-more
          v-if="total > stores.length"
          :skip-slide-into-view="true"
          :api-url="apiUrl"
          :on-white-bg="true"
          @showMoreResult="updateResults(searchQuery, true, ...arguments)"
        />
      </div>
    </div>

    <div
      v-if="error"
      class="technical-error"
    >
      <searchNotification
        :message="noticeMessage"
        :heading="noticeHeading"
        :is-error-message="true"
      />
    </div>
  </section>
</template>

<script>
  import searchNotification from "@/vue/components/common/searchNotification"
  import showMore from '@/vue/components/common/showMore'
  import ga from '@/analytics/dataLayerTopNav.js'
  import debounce from "lodash.debounce"
  import spinner from './spinner'
  import storeList from './storeList'
  import '#/Positions/MyPosition'
  import '#/Navigation/Search'
  import '#/Search/Search'

  export default {
    name: 'FindStoreTab',
    components: {
      searchNotification,
      storeList,
      spinner,
      showMore,
    },
    props: {
      currentStoreId: {
        type: String,
        default: ""
      },
      activeTab: {
        type: Boolean,
        default: false
      },
    },
    data () {
      return {
        searchQuery: '',
        stores: null,
        error: null,
        noticeMessage: "",
        noticeHeading: "",
        loading: false,
        baseApiUrl: '',
        total: 0,
        take: 5,
        skip: 0,
        resultHeading: '',
        isGeoSearch: false
      }
    },
    computed: {
      apiUrl () {
        return `${this.baseApiUrl}&take=${this.take}&skip=${this.skip}`
      }
    },
    watch: {
      searchQuery () {
        this.getStores()
      },
      active: function (newVal) {
        if (newVal == false) {
          this.$emit('openstate', false)
        }
      },
      activeTab: function (newVal) {
        // Focus when switching tabs. Since we use v-show (to keep searches in memory) we need to trigger focus this way.
        if (newVal) {
          document.getElementById("store-search").focus()
        }
      },
    },
    methods: {
      reset () {
          this.noticeHeading = ""
          this.noticeMessage = ""
          this.loading = true
          this.stores = null
          this.error = false
          this.skip = 0
      },
      emitSelectedStore (store) {
        this.$emit('selectedStore', store, 'hitta butik')
      },
      getStoresByLocation () {
        // If geolocation is available in browser
        if (navigator.geolocation) {
          this.reset()
          this.searchQuery = ""
          this.isGeoSearch = true

          // Ask user to allow us to fetch geolocation data
          navigator.geolocation.getCurrentPosition((position) => {
            ga.storeCloseToMe()
            this.baseApiUrl = `${this.$root.envConfig.baseUrl}/api/store/search?query=*&lon=${position.coords.longitude}&lat=${position.coords.latitude}`
            this.doGetData("*")
          },() => {
            // If user has disallowed us access
            this.noticeHeading = "Platsdata avstängt"
            this.noticeMessage = "Du har valt att inte tillåta oss att använda dina platsdata. Om du ångrat dig ändrar du detta själv i din webbläsares plats-inställningar."
            this.error = true
            this.loading = false
          })
        } else {
          this.loading = false
        }
      },
      getStores: debounce(async function () {
        if (this.searchQuery.length >= 3) {
          this.reset()
          this.isGeoSearch = false
          this.baseApiUrl = `${this.$root.envConfig.baseUrl}/api/store/search?query=${this.searchQuery}`
          this.doGetData(this.searchQuery)
        }
      }, 300),
      doGetData (searchQuery) {
        fetch(this.apiUrl)
          .then((response) => response.json())
          .then((data) => this.updateResults(searchQuery, false, data))
          .catch((err) => {
            this.loading = false
            this.error = err
          })
      },
      /* updateResults
       * query (string) - search term
       * showMore (bool) - if update is triggered by show more button (for ga)
       * result (json) - result list
       * keyboardClick (bool) - if search was triggered by keyboard (for accessability) */
      updateResults (query, showMore, result, keyboardClick) {
        const stores = result.Documents
        this.total = result.Stats.TotalHits
        this.skip += this.take
        this.loading = false

        // Set correct header depending on geo search
        if (this.isGeoSearch) {
          this.resultHeading = 'Butiker nära dig'
        } else {
          this.resultHeading = this.total + ' resultat'
        }

        if (showMore) {
          ga.storeSearch(true, query, !!stores.length > 0)
        } else {
          ga.storeSearch(false, query, !!stores.length > 0)
        }

        // Stores are default null, so first time we need to create the array
        if (this.stores === null) {
          this.stores = stores
        } else {
          this.stores.push(...stores)
        }

        if (keyboardClick) {
          this.setFocus(stores.length)
        }
      },
      setFocus (resultLength) {
        // Set focus on first "new" element in the show more list if keyboard click (accessibility)
        this.$nextTick(() => {
          const el = document.querySelector(`.store-list .store-list__row:nth-last-of-type(${resultLength}) button`)

          if (el) {
            el.focus()
          } else {
            throw new Error('Det gick inte att sätta fokus i första nya elementet')
          }
        })
      },
      selectStore (store) {
        ga.selectNewStore(store)
        this.$emit('update-selected-store', store)
        this.$emit('openstate', false)
      }
    }
  }
</script>

<style lang="scss">
.find-store {
  @include media-breakpoint-up(lg) {
    .e6-modal-container {
      min-height: 32.5rem;
    }
  }

  .store-list + .show-more-button {
    margin-top: 1.5rem;
    margin-bottom: 0;
  }

  &__label-area {
    display: inline-flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    width: 100%;

    .button.button--32 {
      padding: 0;
      border-radius: 0;
    }
  }

  &__label-text {
    @include font(text, 2, "black");

    text-transform: uppercase;
    margin-top: auto;
  }

  &__search-input {
    margin: 0;

    input[type='search']::-ms-clear {
      display: none;
      width: 0;
      height: 0;
    }

    input[type='search']::-ms-reveal {
      display: none;
      width: 0;
      height: 0;
    }

    /* clears the 'X' from Chrome */
    input[type='search']::-webkit-search-decoration,
    input[type='search']::-webkit-search-cancel-button,
    input[type='search']::-webkit-search-results-button,
    input[type='search']::-webkit-search-results-decoration {
      display: none;
    }

    & .input-text__element {
      background: $color-white;

      &.store-search__input {
        box-sizing: border-box;
        padding-left: 2.5rem;
        padding-right: 4.5rem;
        appearance: none;
      }

      &:focus {
        border-bottom: 2px solid $color-red;

        &.store-search__input {
          padding-left: 2.5rem;
          padding-right: 4.5rem;
          color: $color-black;
        }
      }
    }

    &--button {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translate(-8px, -50%);
    }

    & .search-icon {
      width: 1.25rem;
      height: 1.25rem;
      position: absolute;
      left: 0.75rem;
      top: 50%;
      transform: translate(0, -50%);
    }
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 15rem;
  }

  &__results {
    margin-right: -1rem;
    padding-right: 1rem;
    margin-top: 1rem;
    overflow-y: auto;

    @include media-breakpoint-up(md) {
      margin-top: 1.5rem;
      max-height: 360px;
    }

    // on small laptops we remove inner scroll
    @media screen and (min-width: 900px) and (max-height: 665px) {
      max-height: none;
    }

    .not-found {
      background-color: $color-blue-lightest;
      display: flex;
      align-items: center;
      color: $color-blue-dark;
      padding: 1rem;
      white-space: pre-wrap;
      border-radius: 0.25rem;

      @include media-breakpoint-up(lg) {
        padding: 1.5rem;
      }

      .no-result--text-wrapper {
        display: flex;
        flex-direction: column;

        h3 {
          margin: 0 0 0.25rem 0;
        }

        p {
          margin: 0;
        }
      }

      svg {
        min-width: 2rem;
        height: 2rem;
        max-width: 2rem;
        margin-right: 1rem;

        @include media-breakpoint-up(lg) {
          min-width: 2.5rem;
          height: 2.5rem;
          max-width: 2.5rem;
        }
      }
    }
  }

  .technical-error {
    margin-top: 1rem;
  }
}
</style>
